import React from "react";
import styled from "styled-components";
import { PromotionSelectButton } from "./PromotionSelectButton/PromotionSelectButton";
import { PromotionDetails, PromotionSelectorStyles } from "../../types";
import { useTranslation } from "react-i18next";

const PromotionContainer = styled.div<{
  styles: PromotionSelectorStyles;
  isPromotionSelected: boolean;
}>`
  padding: 10px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ styles }) => styles.PromoBoxBorderColour};
  border-radius: ${({ styles }) => `${styles.PromoBoxBorderRadius}px`};
  background-color: ${({ styles, isPromotionSelected }) =>
    isPromotionSelected
      ? styles.PromoSelectedBackgroundColour
      : styles.PromoOptionBackgroundColour};
  color: ${({ styles, isPromotionSelected }) =>
    isPromotionSelected
      ? styles.PromoSelectedTextColour
      : styles.PromoOptionTextColour};

  &:hover {
    background-color: ${({ styles }) =>
      styles.PromoOptionHoverBackgroundColour};
    color: ${({ styles }) => styles.PromoOptionHoverTextColour};
  }
`;

const PromotionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const PromotionInfo = styled.div`
  flex: 1;
  margin-right: 10px;
`;

const PromotionName = styled.label`
  font-size: 24px;
`;

const PromotionPrice = styled.span`
  font-size: 24px;
  margin-left: 5px;
`;

const PromotionDescription = styled.label`
  margin-top: 5px;
  font-size: 14px;
`;

const PromotionMenuLink = styled.a`
  margin-top: 5px;
  font-size: 14px;
  color: inherit;
`;

export type PromotionSelectorProps = {
  promotion: PromotionDetails;
  isPromotionSelected: boolean;
  styles: PromotionSelectorStyles;
  setSelectedPromotion: React.Dispatch<
    React.SetStateAction<PromotionDetails | null>
  >;
};

export const PromotionSelector: React.FC<PromotionSelectorProps> = ({
  promotion,
  isPromotionSelected,
  styles,
  setSelectedPromotion,
}) => {
  const { t } = useTranslation();
  const setPromotionAsSelected = (isSelected: boolean) => {
    if (isPromotionSelected) {
      setSelectedPromotion(null);
    } else {
      setSelectedPromotion(promotion);
    }
    return isSelected;
  };

  return (
    <PromotionContainer
      styles={styles}
      isPromotionSelected={isPromotionSelected}
      data-id={`promotion-${promotion.promotionId}`}
    >
      <PromotionRow>
        <PromotionInfo>
          <PromotionName>{promotion.promotionName}</PromotionName>
          {promotion.promotionPrice && (
            <PromotionPrice>({promotion.promotionPrice})</PromotionPrice>
          )}
        </PromotionInfo>
        <PromotionSelectButton
          ariaLabel={
            isPromotionSelected
              ? t("UnselectItem", { item: promotion.promotionName })
              : t("SelectItem", { item: promotion.promotionName })
          }
          isSelected={isPromotionSelected}
          onClick={(isSelected) => setPromotionAsSelected(isSelected)}
          dataId={`${isPromotionSelected ? "selected-" : ""}promotion-button-${promotion.promotionId}`}
        />
      </PromotionRow>
      {promotion.promotionDescription && (
        <PromotionDescription>
          {promotion.promotionDescription}
        </PromotionDescription>
      )}
      {promotion.menuUrl && (
        <PromotionDescription>
          <PromotionMenuLink href={promotion.menuUrl} target="_blank">
            {t("MenuPreview")}
          </PromotionMenuLink>
        </PromotionDescription>
      )}
    </PromotionContainer>
  );
};

export default PromotionSelector;
